import { IconButton } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { InputContainer } from '@creditornot/cb-form';
import { CaretDown, CaretUp, InfoCircleFilled, Trashbin } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Popover } from '@creditornot/cb-popover';
import { InputRadio } from '@creditornot/cb-radio';
import { Select } from '@creditornot/cb-select';
import React from 'react';
import { Field } from 'react-final-form';
import { FieldArrayRenderProps } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import { Typography as T } from 'common/typography';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { VatCategories, useVatCategories } from 'modules/menu/components/VatCategories';
import {
  INPUT_WIDTH,
  OPTION_EDIT_DEPOSIT_COLUMN_WIDTH,
  OPTION_EDIT_VAT_COLUMN_WIDTH,
} from 'modules/menu/options/components/constants';
import { FormOptionValue } from 'modules/menu/types';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { useDeposits } from '../../hooks/useDeposits';

export type Props = {
  className?: string;
  country: Country;
  fields: FieldArrayRenderProps<FormOptionValue[], HTMLElement>['fields'];
  index: number;
  isChoice: boolean;
  isMultiChoice: boolean;
  name: string;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
};

const getValueNamePlaceholderId = (booleanOption: boolean, optionValueIndex: number): string => {
  return booleanOption
    ? ['menu-option.default-false-label', 'menu-option.default-true-label'][optionValueIndex]
    : [
        'menu-option.option-value-name-example-salad',
        'menu-option.option-value-name-example-tomato',
        'menu-option.option-value-name-example-cucumber',
      ][optionValueIndex % 3];
};

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
  padding: 12px 16px;

  & + & {
    margin-block-end: 8px;
  }
`;

const Fields = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 100%;
`;

const FirstFieldGroup = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const SecondFieldGroup = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 4px 16px 0 0;
`;

const FieldLabel = styled(T.Small)`
  margin-block-end: 4px;
  color: ${cbSemanticCssVars.textPrimary};
  display: flex;
  align-items: center;
`;

const OptionValueName = styled.div`
  flex: 1 1 auto;
  width: 100%;
  hyphens: auto;
`;

const OptionValuePosId = styled.div`
  flex: 0 0 auto;
  hyphens: auto;
`;

const OptionValueCount = styled.div`
  flex: 0 0 auto;
  width: 100px;
`;

const OptionValueHideOnIPad = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: flex-start;
  hyphens: auto;
`;

const OptionValueDisabled = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: flex-start;
  hyphens: auto;
`;

const OptionValuePrice = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;

  > div {
    width: ${INPUT_WIDTH}px;
  }
`;

const OptionValueDeposit = styled.div`
  flex: 1 1 auto;
  width: ${OPTION_EDIT_DEPOSIT_COLUMN_WIDTH}px;
  hyphens: auto;
`;

const OptionValueVatPercentage = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-start;

  > div {
    width: ${OPTION_EDIT_VAT_COLUMN_WIDTH}px;
  }
`;

const OptionValueDefault = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: flex-start;
  hyphens: auto;
`;

const Movers = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-inline-end: 16px;
`;

const StyledToolTip = styled(Popover)`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-inline-start: 4px;
`;

const InfoToolTipIcon = styled(InfoCircleFilled).attrs({ width: 12 })`
  fill: ${cbSemanticCssVars.textSecondary};
  width: 100%;
  height: auto;
`;

const TooltipContent = styled.div`
  padding: 16px;
  max-width: 300px;
`;

const Currency = styled(T.Small)`
  margin-inline-start: 4px;
`;

const DeleteButton = styled(IconButton).attrs({ size: 'small', variant: 'red' })``;

export const OptionValuePartialForm = ({
  className,
  fields,
  index,
  isChoice,
  isMultiChoice,
  name,
  onMoveUp,
  onMoveDown,
  onDelete,
}: Props) => {
  const intl = useIntl();
  const menuPermissions = useOfferingMenuPermissionsContext();
  const menuFeatures = useOfferingMenuFeaturesContext();
  const { currency } = useCountryConfigContext();
  const { data: vatCategories } = useVatCategories();

  const { data: depositOptions } = useDeposits({ enabled: menuFeatures.bottleDeposits });

  return (
    <Root className={className}>
      <Movers>
        <IconButton onClick={onMoveUp} variant="blue" size="tiny" style={{ marginBlockEnd: '8px' }}>
          <CaretUp />
        </IconButton>
        <IconButton onClick={onMoveDown} variant="blue" size="tiny">
          <CaretDown />
        </IconButton>
      </Movers>
      <Fields>
        <FirstFieldGroup>
          <FieldContainer style={{ flex: '1 1 auto' }}>
            <FieldLabel>
              <FormattedMessage id="menu-option.name-heading" />
            </FieldLabel>
            <OptionValueName>
              <Field name={`${name}.name`}>
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <InputContainer>
                        <Input
                          {...input}
                          placeholder={intl.formatMessage({ id: getValueNamePlaceholderId(!isChoice, index) })}
                        />
                      </InputContainer>
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </OptionValueName>
          </FieldContainer>
          {(isChoice || isMultiChoice) && (
            <FieldContainer>
              <FieldLabel>
                <FormattedMessage id="menu-option.disabled" />
              </FieldLabel>
              <OptionValueDisabled>
                <Field name={`${name}.disabled`}>
                  {field => <InputCheckbox checked={field.input.value} onChange={field.input.onChange} />}
                </Field>
              </OptionValueDisabled>
            </FieldContainer>
          )}
          {menuFeatures.bottleDeposits && (
            <FieldContainer>
              <FieldLabel>
                <FormattedMessage id="menu-item.deposit" />
                <StyledToolTip
                  contentZIndex={100002} // Ensure tooltip is shown over OptionModal
                  content={
                    <TooltipContent>
                      <T.Body3>
                        <FormattedMessage id="menu-item.deposit-tooltip" />
                      </T.Body3>
                    </TooltipContent>
                  }
                  disableContentPadding
                >
                  <InfoToolTipIcon name="info" />
                </StyledToolTip>
              </FieldLabel>
              <OptionValueDeposit>
                <Field name={`${name}.depositPermutationId`}>
                  {field => (
                    <Select
                      clearable={false}
                      disableSearch
                      maxMenuHeight={155}
                      options={depositOptions}
                      {...field.input}
                    />
                  )}
                </Field>
              </OptionValueDeposit>
            </FieldContainer>
          )}
        </FirstFieldGroup>
        <SecondFieldGroup>
          <FieldContainer>
            <FieldLabel>
              <FormattedMessage id="menu-option.vat-header" />
              <StyledToolTip
                contentZIndex={100002} // Ensure tooltip is shown over OptionModal
                content={
                  <TooltipContent>
                    <T.Body3>
                      <FormattedMessage id="menu-option.vat-tooltip" />
                    </T.Body3>
                  </TooltipContent>
                }
                disableContentPadding
              >
                <InfoToolTipIcon name="info" />
              </StyledToolTip>
            </FieldLabel>
            <OptionValueVatPercentage>
              <Field<FormOptionValue['vat']> name={`${name}.vat`}>
                {field => (
                  <RFFFieldErrorWrapper field={field} showErrorImmediately>
                    {input => (
                      <VatCategories
                        {...input}
                        vatCategories={vatCategories?.list}
                        disabled={!menuPermissions.itemVatChange || !menuPermissions.contentEdit}
                        lang={intl.locale}
                        clearable
                      />
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </OptionValueVatPercentage>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>
              <FormattedMessage id="menu-option.price-heading" />
            </FieldLabel>
            <OptionValuePrice>
              <Field name={`${name}.price`}>
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <InputContainer>
                        <Input {...input} placeholder={intl.formatMessage({ id: 'menu.pos-id-placeholder' })} />
                      </InputContainer>
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
              <Currency>{currency}</Currency>
            </OptionValuePrice>
          </FieldContainer>
          {menuPermissions.posIdField && (
            <FieldContainer>
              <FieldLabel>
                <FormattedMessage id="menu.pos-id" />
              </FieldLabel>
              <OptionValuePosId>
                <Field name={`${name}.externalId`}>
                  {field => (
                    <InputContainer>
                      <Input {...field.input} placeholder={intl.formatMessage({ id: 'menu.pos-id-placeholder' })} />
                    </InputContainer>
                  )}
                </Field>
              </OptionValuePosId>
            </FieldContainer>
          )}
          {!isMultiChoice && (
            <FieldContainer>
              <FieldLabel>
                <FormattedMessage id="menu-option.hide-on-ipad" />
              </FieldLabel>
              <OptionValueHideOnIPad>
                <Field name={`${name}.hideWhenSelected`}>
                  {field => <InputCheckbox checked={field.input.value} onChange={field.input.onChange} />}
                </Field>
              </OptionValueHideOnIPad>
            </FieldContainer>
          )}
          {isMultiChoice && (
            <>
              <FieldContainer>
                <FieldLabel>
                  <FormattedMessage id="menu-option.mincount-heading" />
                </FieldLabel>
                <OptionValueCount>
                  <Field name={`${name}.minimumSelections`}>
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => (
                          <InputContainer>
                            <Input {...input} />
                          </InputContainer>
                        )}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </OptionValueCount>
              </FieldContainer>
              <FieldContainer>
                <FieldLabel>
                  <FormattedMessage id="menu-option.maxcount-heading" />
                  <StyledToolTip
                    contentZIndex={100002} // Ensure tooltip is shown over OptionModal
                    content={
                      <TooltipContent>
                        <T.Body3>
                          <FormattedMessage id="menu-option.maxcount-description" />
                        </T.Body3>
                      </TooltipContent>
                    }
                    disableContentPadding
                  >
                    <InfoToolTipIcon name="info" />
                  </StyledToolTip>
                </FieldLabel>
                <OptionValueCount>
                  <Field name={`${name}.maximumSelections`}>
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => (
                          <InputContainer>
                            <Input {...input} />
                          </InputContainer>
                        )}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </OptionValueCount>
              </FieldContainer>
            </>
          )}
          <FieldContainer>
            <FieldLabel>
              <FormattedMessage id="menu-option.default-option-heading" />
            </FieldLabel>
            <OptionValueDefault>
              <Field name="defaultValueIndex" type="radio" value={index}>
                {field => (
                  <InputRadio
                    {...field.input}
                    onChange={e => {
                      field.input.onChange(Number(e.target.value));
                    }}
                  />
                )}
              </Field>
            </OptionValueDefault>
          </FieldContainer>
        </SecondFieldGroup>
      </Fields>
      {isChoice && fields.length && fields.length > 1 && (
        <DeleteButton onClick={onDelete}>
          <Trashbin />
        </DeleteButton>
      )}
    </Root>
  );
};
